import { styled } from "@material-ui/core";
import { brand } from "../../theme";

/** Visual shell of the Lotto Loco modal. Holds the reels, header, footer,
 *  and confetti layer. All keyframe references are global (see
 *  ./keyframes.ts) so per-row inline animation durations can compose. */
export const ModalShell = styled("div")(() => ({
  width: "100%",
  maxWidth: 360,
  background: `linear-gradient(180deg, ${brand.amberDark} 0%, #2D1F08 100%)`,
  borderRadius: 22,
  overflow: "hidden",
  border: `1.5px solid ${brand.amber}55`,
  boxShadow: `0 20px 60px rgba(0,0,0,0.5), 0 0 0 1px ${brand.amber}22`,
  position: "relative",
  fontFamily: "inherit",
  animation: "lottoLocoBounce 0.4s ease-out",
  "& .confetti-layer": {
    position: "absolute",
    inset: 0,
    pointerEvents: "none",
    overflow: "hidden",
  },
  "& .confetti-piece": {
    position: "absolute",
    top: -10,
    borderRadius: 1,
    animationName: "lottoLocoConfetti",
    animationTimingFunction: "ease-in",
    animationFillMode: "forwards",
  },
  "& .header": {
    padding: "18px 20px 8px",
    textAlign: "center",
    position: "relative",
  },
  "& .header-title": {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    marginBottom: 6,
  },
  "& .slot-icon": { fontSize: 22 },
  "& .slot-icon--spinning": {
    animation: "lottoLocoShakeFast 0.4s ease-in-out infinite",
  },
  "& .header-name": {
    fontSize: 18,
    fontWeight: 900,
    color: brand.amber,
    letterSpacing: 1.2,
  },
  "& .header-sub": {
    fontSize: 12,
    color: "#C4B8A8",
    lineHeight: 1.4,
  },
  "& .reels": {
    margin: "10px 14px",
    background: "#0E0903",
    borderRadius: 12,
    border: `1px solid ${brand.amber}33`,
    padding: "10px 8px",
    maxHeight: 340,
  },
  "& .reels--reveal": { overflowY: "auto" },
  "& .reels--ready, & .reels--spinning": { overflow: "hidden" },
  "& .row": {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "8px 10px",
  },
  "& .row + .row": {
    borderTop: `1px dashed ${brand.amber}22`,
  },
  "& .row-num": {
    width: 24,
    height: 24,
    borderRadius: 6,
    background: brand.amber,
    color: brand.amberDark,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 11,
    fontWeight: 800,
    flexShrink: 0,
  },
  "& .reel": {
    flex: 1,
    height: 28,
    overflow: "hidden",
    position: "relative",
    background: brand.amberDark,
    borderRadius: 6,
    border: `1px solid ${brand.amber}22`,
    display: "flex",
    alignItems: "center",
    paddingLeft: 10,
  },
  "& .reel-strip": {
    display: "flex",
    flexDirection: "column",
    animationName: "lottoLocoSpin",
    animationTimingFunction: "linear",
    animationIterationCount: "infinite",
  },
  "& .reel-strip-item": {
    height: 28,
    display: "flex",
    alignItems: "center",
    fontSize: 13,
    color: brand.amber,
    fontWeight: 600,
    whiteSpace: "nowrap",
  },
  "& .reel-text": { fontSize: 13, fontWeight: 700 },
  "& .reel-text--placeholder": {
    color: brand.textDis,
    letterSpacing: 2,
  },
  "& .reel-text--reveal": {
    color: brand.amber,
    animation: "lottoLocoFadeIn 0.3s ease-out",
  },
  "& .more-rounds": {
    textAlign: "center",
    padding: "6px 0 2px",
    fontSize: 10,
    color: brand.textDis,
    letterSpacing: 1,
  },
  "& .footer": {
    padding: "10px 16px 18px",
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  "& .btn-primary": {
    width: "100%",
    height: 50,
    borderRadius: 12,
    background: `linear-gradient(135deg, ${brand.amber} 0%, ${brand.amberDeep} 100%)`,
    color: brand.amberDark,
    border: "none",
    fontSize: 14,
    fontWeight: 900,
    letterSpacing: 1.4,
    textTransform: "uppercase",
    cursor: "pointer",
    boxShadow: `0 4px 18px ${brand.amber}66`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    fontFamily: "inherit",
  },
  "& .btn-secondary": {
    width: "100%",
    height: 38,
    background: "transparent",
    color: brand.amber,
    border: `1px solid ${brand.amber}55`,
    borderRadius: 10,
    fontSize: 12,
    fontWeight: 800,
    letterSpacing: 0.6,
    textTransform: "uppercase",
    cursor: "pointer",
    fontFamily: "inherit",
  },
  "& .btn-link": {
    width: "100%",
    height: 38,
    background: "transparent",
    color: "#C4B8A8",
    border: "none",
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: 0.6,
    textTransform: "uppercase",
    cursor: "pointer",
    fontFamily: "inherit",
  },
  "& .rolling": {
    textAlign: "center",
    fontSize: 11,
    color: "#C4B8A8",
    letterSpacing: 1,
    fontWeight: 700,
    padding: "8px 0",
  },
}));

export const Backdrop = styled("div")(() => ({
  position: "fixed",
  inset: 0,
  background: "rgba(20,12,2,0.72)",
  backdropFilter: "blur(4px)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 16,
  zIndex: 1300,
}));
